import React, { useState } from 'react';
import Card from 'src/components/Card';
import Avatar from 'src/components/Avatar';
import Button from 'src/components/Button';
import FontAwesomeIcon from 'src/components/Icon';
import OpenContactLink from 'src/components/OpenContactLink/OpenContactLink';
import EmptyAvatar from 'src/components/EmptyAvatar/EmptyAvatar';
import Skeleton from 'src/components/Skeleton/Skeleton';
import SrDetailContact from 'src/pages/ServiceRequest/components/SrDetailContact/SrDetailContact';
import { isRole } from 'src/helpers/permissions';
import { USER_ROLES } from 'src/common/constants';
import {
  Subscriber,
  SaveSubscribersResult,
} from './CustomHooks/useServiceRequestSubscribers';
import SubscribersViewAllDialog from './SubscribersViewAllDialog';
import SubscribersEditDialog from './SubscribersEditDialog';
import NewBadge from 'src/components/NewBadge/NewBadge';
import './SubscribersCard.scss';

interface SubscribersCardProps {
  serviceRequest: any;
  user: any;
  subscribers: Subscriber[];
  loading: boolean;
  saving: boolean;
  onSaveSubscribers: (userIds: string[]) => Promise<SaveSubscribersResult>;
}

interface SubscribersEditButtonProps {
  onClick: () => void;
}

interface SubscriberRowProps {
  subscriber: Subscriber;
  serviceRequestId: string;
}

interface SubscribersOverflowLinkProps {
  count: number;
  onClick: () => void;
}

const VISIBLE_PREVIEW_COUNT: number = 1;

const EDIT_ROLES: string[] = [
  USER_ROLES.PROPERTY_DEVELOPER,
  USER_ROLES.SUPER_ADMIN,
  USER_ROLES.ADMIN,
  USER_ROLES.SERVICE_MANAGER,
  USER_ROLES.SERVICE_PROVIDER,
];

const buildAvatarBadge = (
  role: string
): 'service-provider' | 'property-manager' => {
  if (role === 'Service Provider' || role === 'Service Manager') {
    return 'service-provider';
  }

  return 'property-manager';
};

const SubscribersLoadingCard = () => (
  <Card
    title="Subscribers"
    body={
      <div className="subscribers-card-body">
        <div className="subscribers-card-list">
          <div className="subscribers-card-row">
            <SrDetailContact loading name={undefined} />
            <Skeleton
              variant="circle"
              width="2.4rem"
              height="2.4rem"
              className="subscribers-card-row-trailing-skeleton"
            />
          </div>
        </div>
      </div>
    }
  />
);

const SubscribersEditButton = ({ onClick }: SubscribersEditButtonProps) => (
  <div className="subscribers-card-edit-button">
    <Button
      color="semantic"
      onClick={onClick}
      label={<FontAwesomeIcon glyph="pencil" weight="light" />}
    />
  </div>
);

const SubscribersEmptyRow = () => (
  <div className="subscribers-card-list">
    <div className="subscribers-card-row">
      <SrDetailContact name="No subscribers yet" avatar={<EmptyAvatar />} />
    </div>
  </div>
);

const SubscriberRow = ({ subscriber, serviceRequestId }: SubscriberRowProps) => (
  <div className="subscribers-card-row">
    <SrDetailContact
      name={subscriber.name}
      role={subscriber.role}
      avatar={
        <Avatar
          url={undefined}
          badge={buildAvatarBadge(subscriber.role)}
          size={undefined}
        />
      }
    />
    <OpenContactLink
      userId={subscriber.userId}
      name={subscriber.name}
      navOrigin={{ kind: 'serviceRequest', serviceRequestId }}
    />
  </div>
);

const SubscribersOverflowLink = ({
  count,
  onClick,
}: SubscribersOverflowLinkProps) => (
  <button
    type="button"
    className="subscribers-card-more-link"
    onClick={onClick}
  >
    <span>+{count} more</span>
  </button>
);

const SubscribersCard = ({
  serviceRequest,
  user,
  subscribers,
  loading,
  saving,
  onSaveSubscribers,
}: SubscribersCardProps) => {
  const [isViewAllOpen, setIsViewAllOpen] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);

  if (loading) {
    return <SubscribersLoadingCard />;
  }

  const canEdit: boolean = isRole(EDIT_ROLES, user);

  const hasSubscribers: boolean = subscribers.length > 0;

  const visibleSubscribers: Subscriber[] = subscribers.slice(
    0,
    VISIBLE_PREVIEW_COUNT
  );

  const overflowCount: number = subscribers.length - VISIBLE_PREVIEW_COUNT;

  return (
    <Card
      title={
        <>
          Subscribers <NewBadge className="subscribers-card-new-badge" />
        </>
      }
      body={
        <div className="subscribers-card-body">
          {canEdit && (
            <SubscribersEditButton onClick={() => setIsEditOpen(true)} />
          )}

          {!hasSubscribers && <SubscribersEmptyRow />}

          {hasSubscribers && (
            <>
              <div className="subscribers-card-list">
                {visibleSubscribers.map((sub: Subscriber) => (
                  <SubscriberRow
                    key={sub.userId}
                    subscriber={sub}
                    serviceRequestId={serviceRequest.id}
                  />
                ))}
              </div>

              {overflowCount > 0 && (
                <SubscribersOverflowLink
                  count={overflowCount}
                  onClick={() => setIsViewAllOpen(true)}
                />
              )}
            </>
          )}

          {isViewAllOpen && (
            <SubscribersViewAllDialog
              isOpen={isViewAllOpen}
              subscribers={subscribers}
              serviceRequestId={serviceRequest.id}
              onClose={() => setIsViewAllOpen(false)}
            />
          )}

          {isEditOpen && (
            <SubscribersEditDialog
              isOpen={isEditOpen}
              serviceRequest={serviceRequest}
              subscribers={subscribers}
              saving={saving}
              onSave={onSaveSubscribers}
              onClose={() => setIsEditOpen(false)}
            />
          )}
        </div>
      }
    />
  );
};

export default SubscribersCard;
